/**
 * Industrial launch-context isolation for multi-app / multi-tenant tabs.
 *
 * Priority (resolve):
 *   1. URL query  (explicit switch / portal launch — always wins when complete)
 *   2. sessionStorage (same-tab refresh; per-tab so HOMS ≠ HPRMS)
 *   3. empty      → anonymous / KeycloakForm
 *
 * Persist buckets stay keyed by company + appType + instanceId so
 * parallel tabs never overwrite each other's auth.
 *
 * Later industrial step: subdomain-per-app; this layer stays compatible.
 */

const SESSION_KEY = "hulm:launchContext";

const normalizeAppType = (value) =>
  String(value || "")
    .trim()
    .replace(/-+$/, "");

const normalizePart = (value) => String(value || "").trim();

export const isCompleteLaunchContext = (ctx = {}) =>
  Boolean(normalizePart(ctx.companyName) && normalizePart(ctx.instanceId));

export const normalizeLaunchContext = (raw = {}) => ({
  companyName: normalizePart(raw.companyName),
  instanceId: normalizePart(raw.instanceId),
  appType: normalizeAppType(raw.appType),
});

export const launchContextsEqual = (a = {}, b = {}) => {
  const left = normalizeLaunchContext(a);
  const right = normalizeLaunchContext(b);
  return (
    left.companyName === right.companyName &&
    left.instanceId === right.instanceId &&
    left.appType === right.appType
  );
};

export const readLaunchContextFromUrl = (search = window.location.search) => {
  const url = new URLSearchParams(search);
  return normalizeLaunchContext({
    companyName: url.get("companyName"),
    instanceId: url.get("instanceId"),
    appType: url.get("appType"),
  });
};

export const readLaunchContextFromSession = () => {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return normalizeLaunchContext({});
    return normalizeLaunchContext(JSON.parse(raw));
  } catch (_) {
    return normalizeLaunchContext({});
  }
};

export const writeLaunchContextToSession = (ctx) => {
  const normalized = normalizeLaunchContext(ctx);
  if (!isCompleteLaunchContext(normalized)) return false;
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(normalized));
    return true;
  } catch (_) {
    return false;
  }
};

/**
 * Resolve active tenant for this tab.
 * URL wins when complete (company switch / new instance).
 * Otherwise reuse this tab's remembered context (refresh survival).
 */
export const resolveLaunchContext = (search = window.location.search) => {
  const fromUrl = readLaunchContextFromUrl(search);
  if (isCompleteLaunchContext(fromUrl)) {
    writeLaunchContextToSession(fromUrl);
    return fromUrl;
  }

  const fromSession = readLaunchContextFromSession();
  if (isCompleteLaunchContext(fromSession)) {
    return fromSession;
  }

  return normalizeLaunchContext({});
};

/**
 * Remember + keep the address bar bookmarkable after in-app navigation.
 * Uses history.replaceState so React Router path is preserved.
 */
export const syncLaunchContextToUrl = (ctx = resolveLaunchContext()) => {
  const normalized = normalizeLaunchContext(ctx);
  if (!isCompleteLaunchContext(normalized) || typeof window === "undefined") {
    return false;
  }

  try {
    const url = new URL(window.location.href);
    const current = normalizeLaunchContext({
      companyName: url.searchParams.get("companyName"),
      instanceId: url.searchParams.get("instanceId"),
      appType: url.searchParams.get("appType"),
    });

    if (launchContextsEqual(current, normalized)) {
      writeLaunchContextToSession(normalized);
      return false;
    }

    url.searchParams.set("companyName", normalized.companyName);
    url.searchParams.set("instanceId", normalized.instanceId);
    if (normalized.appType) {
      url.searchParams.set("appType", normalized.appType);
    } else {
      url.searchParams.delete("appType");
    }

    const next = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState(window.history.state, "", next);
    writeLaunchContextToSession(normalized);
    return true;
  } catch (_) {
    writeLaunchContextToSession(normalized);
    return false;
  }
};

/** Build ?companyName=&instanceId=&appType= for Links / history.push */
export const buildLaunchSearch = (ctx = resolveLaunchContext()) => {
  const normalized = normalizeLaunchContext(ctx);
  if (!isCompleteLaunchContext(normalized)) return "";
  const params = new URLSearchParams();
  params.set("companyName", normalized.companyName);
  params.set("instanceId", normalized.instanceId);
  if (normalized.appType) params.set("appType", normalized.appType);
  return `?${params.toString()}`;
};

/**
 * Attach launch query to a react-router `to` value without dropping
 * an existing search string on the destination.
 */
export const withLaunchSearch = (to, ctx = resolveLaunchContext()) => {
  const launchSearch = buildLaunchSearch(ctx);
  if (!launchSearch) return to;

  if (typeof to === "string") {
    if (!to) return launchSearch;
    const hashIndex = to.indexOf("#");
    const hash = hashIndex >= 0 ? to.slice(hashIndex) : "";
    const withoutHash = hashIndex >= 0 ? to.slice(0, hashIndex) : to;
    const qIndex = withoutHash.indexOf("?");
    if (qIndex >= 0) {
      // Destination already has query — leave it; launch params live on window via sync.
      return to;
    }
    return `${withoutHash}${launchSearch}${hash}`;
  }

  if (to && typeof to === "object") {
    if (to.search) return to;
    return { ...to, search: launchSearch.slice(1) };
  }

  return to;
};

/**
 * Keycloak clientId format: {appType}-{instanceId}
 * Example: HPRMS-d3f89631-5704-4ea1-94f5-be03fe6e1c79
 */
export const buildKeycloakClientId = (appType, instanceId) => {
  const app = normalizeAppType(appType);
  const id = normalizePart(instanceId);
  if (!app || !id) return "";
  if (id.startsWith(`${app}-`)) return id;
  return `${app}-${id}`;
};

/** redux-persist `key` (becomes localStorage item persist:<key>) */
export const getReduxPersistKey = (search = window.location.search) => {
  const { companyName, instanceId, appType } = resolveLaunchContext(search);
  if (!companyName || !instanceId) {
    return "root:anonymous";
  }
  const app = appType || "unknown";
  return `root:${companyName}:${app}:${instanceId}`;
};

/** Full localStorage key used by redux-persist */
export const getPersistStorageItemKey = (search = window.location.search) =>
  `persist:${getReduxPersistKey(search)}`;
